import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { IColumn } from '@feathr/components';
import {
  Button,
  CopyToClipboardButton,
  Icon,
  Pill,
  TableColumnHeader,
  Text,
  Tooltip,
} from '@feathr/components';
import { moment } from '@feathr/hooks';

import { formatCurrency } from '../../GrowthCreditsPage/RedeemCreditsModal/RedeemCreditsModal.utils';
import type { IDonationCreatedEvent } from '../../hooks';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './TransactionColumns.css';

type TLocalUrl = (path: string) => string;

export type ITransactionColumn = IColumn<IDonationCreatedEvent> & {
  id: string;
  isEssential?: boolean;
};

const ESSENTIAL_COLUMN_IDS = ['time', 'name', 'amount'];
const DEFAULT_COLUMN_IDS = ['time', 'name', 'donation_type', 'form_name', 'amount'];

function formatDateTime(dateString: string): string {
  return moment(dateString).format('MMM D, YYYY, h:mm A');
}

function formatPaymentMethod(method: 'credit_card' | 'ach', t: TFunction): string {
  return method === 'credit_card' ? t('Credit card') : t('ACH');
}

function getAllColumns(t: TFunction, localUrl?: TLocalUrl): ITransactionColumn[] {
  return [
    {
      id: 'time',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Date and Time') }),
      headerClassName: classNames(tableStyles.sort, styles.leftAlignedHeader),
      accessor: 'created_at',
      isEssential: true,
      checkboxLabel: t('Date and Time'),
      Cell: ({ original }): JSX.Element => (
        <div className={styles.timeCell}>
          <Text.DefaultRegular>{formatDateTime(original.created_at)}</Text.DefaultRegular>
          {original.isRealtime && (
            <Pill theme={original.isTestEvent ? 'yellow' : 'cyan'}>
              {original.isTestEvent ? t('Test') : t('New')}
            </Pill>
          )}
        </div>
      ),
    },
    {
      id: 'name',
      Header: t('Donor Name'),
      headerClassName: classNames(styles.leftAlignedHeader, styles.mediumWeight),
      accessor: (row): string => `${row.donor_first_name} ${row.donor_last_name}`,
      sortable: false,
      isEssential: true,
      checkboxLabel: t('Donor Name'),
      Cell: ({ original }): JSX.Element => {
        const fullName = `${original.donor_first_name} ${original.donor_last_name}`.trim();
        const displayName = fullName || t('Anonymous');

        if (original.person_id && localUrl) {
          return (
            <Button
              className={styles.nameLink}
              link={localUrl(`/data/people/${original.person_id}`)}
              type={'link'}
            >
              {displayName}
            </Button>
          );
        }
        return <Text.DefaultRegular>{displayName}</Text.DefaultRegular>;
      },
    },
    {
      id: 'donation_type',
      Header: TableColumnHeader({ sortType: 'alpha', title: t('Type') }),
      headerClassName: classNames(tableStyles.sort, styles.leftAlignedHeader),
      accessor: 'donation_type',
      isEssential: false,
      checkboxLabel: t('Type'),
      Cell: ({ original }): JSX.Element => (
        <Text.DefaultRegular>
          {original.donation_type === 'recurring' ? t('Recurring') : t('One-time')}
        </Text.DefaultRegular>
      ),
    },
    {
      id: 'form_name',
      Header: t('Form'),
      headerClassName: classNames(styles.leftAlignedHeader, styles.mediumWeight),
      accessor: 'form_name',
      sortable: false,
      isEssential: false,
      checkboxLabel: t('Form'),
      Cell: ({ original }): JSX.Element => {
        if (original.form_id && localUrl) {
          return (
            <Button
              className={styles.nameLink}
              link={localUrl(`/fundraise/${original.form_id}/edit`)}
              type={'link'}
            >
              {original.form_name}
            </Button>
          );
        }
        return <Text.DefaultRegular>{original.form_name}</Text.DefaultRegular>;
      },
    },
    {
      id: 'payment_method',
      Header: t('Payment Method'),
      headerClassName: classNames(styles.leftAlignedHeader, styles.mediumWeight),
      accessor: 'payment_method',
      sortable: false,
      isEssential: false,
      checkboxLabel: t('Payment Method'),
      Cell: ({ original }): JSX.Element => (
        <Text.DefaultRegular>{formatPaymentMethod(original.payment_method, t)}</Text.DefaultRegular>
      ),
    },
    {
      id: 'transaction_id',
      Header: t('Transaction ID'),
      headerClassName: classNames(styles.leftAlignedHeader, styles.mediumWeight),
      accessor: 'transaction_id',
      sortable: false,
      isEssential: false,
      checkboxLabel: t('Transaction ID'),
      className: styles.transactionIdColumn,
      Cell: ({ original }): JSX.Element => (
        <Tooltip title={t('Click to copy')}>
          <CopyToClipboardButton
            className={styles.transactionIdCell}
            t={t}
            text={original.transaction_id}
            type={'link'}
          >
            {original.transaction_id}
          </CopyToClipboardButton>
        </Tooltip>
      ),
    },
    {
      id: 'amount',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Donation') }),
      headerClassName: classNames(tableStyles.sort, styles.rightAlignedHeader),
      accessor: 'amount',
      isEssential: true,
      checkboxLabel: t('Donation'),
      className: styles.amountCell,
      Cell: ({ original }): JSX.Element => (
        <Text.DefaultRegular>{formatCurrency(original.amount)}</Text.DefaultRegular>
      ),
    },
    {
      id: 'credits_earned',
      Header: t('Growth Credits'),
      accessor: 'credits_earned',
      sortable: false,
      isEssential: false,
      checkboxLabel: t('Growth Credits'),
      headerClassName: classNames(styles.rightAlignedHeader, styles.mediumWeight),
      className: styles.creditsCell,
      Cell: ({ original }): JSX.Element => (
        <Text.DefaultRegular>{original.credits_earned}</Text.DefaultRegular>
      ),
    },
  ];
}

function getViewColumn(t: TFunction, localUrl?: TLocalUrl): ITransactionColumn {
  return {
    id: 'view',
    Header: t('View'),
    headerClassName: styles.viewHeader,
    accessor: (): null => null,
    sortable: false,
    width: 60,
    className: styles.viewCell,
    Cell: ({ original }): JSX.Element => {
      if (!original.form_id || !localUrl) {
        return <Text.DefaultRegular className={styles.viewDisabled}>—</Text.DefaultRegular>;
      }
      return (
        <Link className={styles.viewLink} to={localUrl(`/fundraise/forms/${original.form_id}`)}>
          <Icon icon={faArrowUpRightFromSquare} />
        </Link>
      );
    },
  };
}

function getVisibleColumns(
  columnIds: string[],
  t: TFunction,
  localUrl?: TLocalUrl,
): ITransactionColumn[] {
  const allColumns = getAllColumns(t, localUrl);
  const visibleColumns = allColumns.filter((col) => columnIds.includes(col.id));
  return [...visibleColumns, getViewColumn(t, localUrl)];
}

function getDefaultColumnIds(): string[] {
  return [...DEFAULT_COLUMN_IDS];
}

function getEssentialColumnIds(): string[] {
  return [...ESSENTIAL_COLUMN_IDS];
}

export { getAllColumns, getDefaultColumnIds, getEssentialColumnIds, getVisibleColumns };
